import { useCallback, useEffect, useState } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { SelectChangeEvent } from '@mui/material';

import { CustomAlert, CustomButton, CustomSelect, CustomTable, Text } from '../../../../shared/uiComponents';
import { Loader } from '../../../../components/StyledComponents';
import { errorNormalizer } from '../../../../shared/Helpers/functions';
import { Notes } from '../../../../shared/Assets/menuIcons';
import SignatureInput from '../../../SOAPNote/components/signatureInput';
import { Headers, TableBody } from './tableInfo';

import { DispatchProperties, useSelector } from '../../../../redux/store';
import { approveSoapNotes, getPendingSoapNotes } from '../../../../redux/State/clientSlice/soapNoteSlice';
import { SoapNotesPaginationProperties } from '../../../../redux/API/ClientAPIHelpers/soapNoteProperties';
import { getUserBTs } from '../../../../redux/State/clientSlice/userClientSlice';

interface ContentProperties {
	onClose: () => void;
	onApprovalSuccess: () => void;
	reportIds: string[];
	refreshParams: SoapNotesPaginationProperties;
}

interface PendingApprovalProperties {
	isOpen: boolean;
	onClose: () => void;
	onRefreshDashboard: () => void;
}

// TODO: For non-credentialed BCBAs, the signature may need to default to the Billing BCBA's name
// (e.g., Chaya/Pesia) instead of the logged-in BCBA's typed input.
// Requirement: "Sessions with not-credentialed BCBAs should have Chaya or Pesia's signature."
const Content = ({ onClose, onApprovalSuccess, reportIds, refreshParams }: ContentProperties) => {
	const dispatch = useDispatch<DispatchProperties>();
	const loading = useSelector((state) => state.soapNote.loading);
	const [signature, setSignature] = useState<string>('');

	const approveHandler = useCallback(() => {
		if (!reportIds.length) return;

		dispatch(approveSoapNotes({ signature, reportIds }))
			.then(unwrapResult)
			.then(() => {
				dispatch(getPendingSoapNotes(refreshParams));
				onClose();
				onApprovalSuccess();
			})
			.catch(errorNormalizer);
	}, [dispatch, onClose, reportIds, refreshParams, signature]);

	return (
		<>
			<Text
				title={`You are approving ${reportIds.length} SOAP note${reportIds.length > 1 ? 's' : ''}. Your signature will be applied to ${reportIds.length > 1 ? 'all selected notes' : 'this note'}.`}
				type="psm"
			/>
			<SignatureInput
				label="BCBA Signature:"
				value={signature}
				setValue={({ target }) => setSignature(target.value.toUpperCase())}
				disabled={false}
				onBlur={() => null}
				error={false}
			/>
			<CustomButton onClick={approveHandler} title="Approve" loading={loading} disabled={!signature} />
		</>
	);
};

const PendingApproval = ({ isOpen, onClose, onRefreshDashboard }: PendingApprovalProperties) => {
	const dispatch = useDispatch<DispatchProperties>();

	const [params, setParams] = useSearchParams();
	const btId = params.get('btId') || '';
	const page = params.get('page') || '1';
	const pageSize = params.get('pageSize') || '8';
	const direction = params.get('direction') || '';
	const orderBy = params.get('orderBy') || 'date';

	const [open, setOpen] = useState<boolean>(false);

	const notes = useSelector((state) => state.soapNote.pendingSoapNotes);
	const loading = useSelector((state) => state.soapNote.loading);
	const user = useSelector((state) => state.account.user);
	const userBTs = useSelector((state) => state.userClient.userBTs);
	const reportIds = useSelector((state) => state.billing.billingFilters.reportIds);

	const openApproveHandler = () => setOpen(true);
	const closeApproveHandler = () => setOpen(false);

	const refreshParams: SoapNotesPaginationProperties = {
		page,
		pageSize,
		direction,
		orderBy,
		userId: btId,
	};

	useEffect(() => {
		if (!isOpen) return;
		dispatch(getPendingSoapNotes(refreshParams));
	}, [dispatch, page, pageSize, direction, orderBy, btId, isOpen]);

	useEffect(() => {
		if (!notes?.totalNumberOfItems || !user?.id) return;
		dispatch(getUserBTs(user.id));
	}, [notes, user, dispatch]);

	const selectBtHandler = (event: SelectChangeEvent<string>) => {
		const { value } = event.target;

		if (value) {
			params.set('btId', value);
		} else {
			params.delete('btId');
		}
		setParams(params);
	};

	const labels = userBTs.map((bt) => ({ id: bt.id, name: bt.fullName }));
	const data = [...labels, { id: '', name: 'Clear' }];

	if (!isOpen) return null;

	return (
		<div className="dashboardCard dashboardCardFullWidth">
			<div className="dashboardHeader" style={{ position: 'relative', paddingRight: '64px' }}>
				<div className="header">
					<div className="headerIcon">
						<Notes />
					</div>
					<Text title="Pending SOAP Notes" type="h4" font="header" fontWeight="semiBold" />
					<Text title="Select notes to approve and sign" type="psm" />
				</div>
				<div style={{ width: '200px', flexShrink: 0 }}>
					<CustomSelect data={data} value={btId} setValue={selectBtHandler} />
				</div>
				{Boolean(reportIds.length) && (
					<div style={{ width: 'max-content' }}>
						<CustomButton onClick={openApproveHandler} title={`Sign Selected (${reportIds.length})`} disabled={!reportIds.length || !notes?.query?.length} />
						<CustomAlert
							open={open}
							onClose={closeApproveHandler}
							title="Approve & Sign"
								Content={({ onClose: closeModal }) => (
								<Content 
									onClose={closeModal} 
									onApprovalSuccess={() => {
										onClose();
										onRefreshDashboard();
									}} 
									reportIds={reportIds} 
									refreshParams={refreshParams} 
								/>
							)}
						/>
					</div>
				)}
				<button
					onClick={onClose}
					style={{
						position: 'absolute',
						top: '20px',
						right: '24px',
						background: 'transparent',
						border: 'none',
						cursor: 'pointer',
						padding: '4px',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						color: '#6B6B6B',
						fontSize: '24px',
						lineHeight: '1',
						width: '32px',
						height: '32px',
						borderRadius: '4px',
						transition: 'background 0.2s',
					}}
					onMouseEnter={(e) => (e.currentTarget.style.background = '#F5F5F5')}
					onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
					aria-label="Close"
				>
					×
				</button>
			</div>
			{loading ? <Loader /> : <CustomTable headers={Headers} TableBody={TableBody} data={notes} loading={loading} hasMobile={false} />}
		</div>
	);
};

export default PendingApproval;
